import * as React from "react";
import {Loader} from "../../../components/Utils/loader/Loader";
import {Display} from "../../../components/Display";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {EmptyState, Icon, icons, infoIcons, Tooltip} from "@vebox/etui-core";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {HorizontalGroupBarChart} from "../../../locations/marketing/charts/HorizontalGroupBarChart";
import {BarChartGroup} from "../../../locations/marketing/charts/BarChartWithGroups";
import {Legend, LegendItem} from "../../../components/charts/Legend";
import {DATA_STATE, useUserDashboardData} from "../../contexts/UsersDashboardContext";
import {ACCESSES_COLOR, ETUI_ICONS_SIZE, NOT_ACCESSES_COLOR} from "../../UserManagement";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;
const MAX_ROWS_NUMBER: number = 8;

export interface AccessesListElement {
    commercialHierarchyName: string,
    commercialHierarchyGuid: string,
    numberOfActiveUsers: number,
    numberOfInactiveUsers: number,
    numberOfUser: number,
}


const reduceData = (data: BarChartGroup[], maxRows: number): BarChartGroup[] => {
    let reducedData: BarChartGroup[] = data;
    if (data.length > maxRows) {
        reducedData = data.slice(0, maxRows - 1);
        const dataToReduce: BarChartGroup[] = data.slice(maxRows - 1);
        const otherRow: BarChartGroup = {
            name: "Other Hierarchy",
            elements: [
                {
                    name: AppTranslations.usersManagement.usersLogging,
                    value: 0,
                    color: ACCESSES_COLOR,
                },
                {
                    name: AppTranslations.usersManagement.usersNotLogging,
                    value: 0,
                    color: NOT_ACCESSES_COLOR,
                }
            ]
        };
        dataToReduce.forEach((row) => {
            otherRow.elements[0].value = otherRow.elements[0].value + row.elements[0].value
            otherRow.elements[1].value = otherRow.elements[1].value + row.elements[1].value
        })
        reducedData = [otherRow, ...reducedData.reverse()];
    }
    return reducedData;
}

const sortDataByAccesses = (data: BarChartGroup[]): BarChartGroup[] => {
    return data.sort((a, b) => b.elements[0].value - a.elements[0].value)
}


const mapDataForChart = (accessesList: AccessesListElement[], maxRows: number): BarChartGroup[] => {
    const data: BarChartGroup[] = accessesList.map((elem) => {
        return {
            name: elem.commercialHierarchyName,
            elements: [
                {
                    name: AppTranslations.usersManagement.usersLogging,
                    value: elem.numberOfActiveUsers,
                    color: ACCESSES_COLOR,
                },
                {
                    name: AppTranslations.usersManagement.usersNotLogging,
                    value: elem.numberOfInactiveUsers,
                    color: NOT_ACCESSES_COLOR,
                }
            ]
        }
    })

    return sortDataByAccesses(reduceData(data, maxRows));
}

const checkIsEmpty = (horizontalGroupBarChart: BarChartGroup[]): boolean => {
    let isEmpty: boolean = true;
    horizontalGroupBarChart.forEach((bar) => {
        if (bar.elements.find((elem) => elem.value > 0)) {
            isEmpty = false;
        }
    })
    return isEmpty
}

const getLegend = (horizontalGroupBarChart: BarChartGroup[]): LegendItem [] => {
    return horizontalGroupBarChart[0]?.elements.map((item) => {
            return {
                color: item.color,
                name: item.name,
                value: 0,
            };
        },
    );
}

export const UsersAccessesByHierarchy: React.FC = () => {

    const [isEmpty, setIsEmpty] = React.useState(false);
    const [legendItems, setLegendItems] = React.useState<LegendItem[]>([]);
    const [horizontalGroupBarChart, setHorizontalGroupBarChartData] = React.useState<BarChartGroup[]>();
    const [selectedElement, setSelectedElement] = React.useState();
    const {userAccessesByHierarchyData, state} = useUserDashboardData().usersAccessesByHierarchyState;

    React.useEffect(() => {
        if (state === DATA_STATE.loaded && userAccessesByHierarchyData !== undefined) {
            setHorizontalGroupBarChartData(mapDataForChart(userAccessesByHierarchyData, MAX_ROWS_NUMBER));
        }
    }, [userAccessesByHierarchyData]);

    React.useEffect(() => {
        if (horizontalGroupBarChart) {

            if (horizontalGroupBarChart.length > 0 && !checkIsEmpty(horizontalGroupBarChart)) {
                setLegendItems(getLegend(horizontalGroupBarChart))
                setIsEmpty(false);
            } else {
                setIsEmpty(true);
            }
        }
    }, [horizontalGroupBarChart]);

    return (
        <VeBoxCard className={"Section"}>
            <VeBoxCard.Header
                title={AppTranslations.usersManagement.usersLoggingByCommercialHierarchyTitle}
                singleLineTitle={true}
                back={<span>
                    <Tooltip
                        message={AppTranslations.usersManagement.hierarchiesWithoutUsersMessage}
                        placement={"left"}
                    >
                    <Icon glyph={icons.INFO_CIRCLE} size={ETUI_ICONS_SIZE}/>
                </Tooltip>
                </span>}
            />
            <VeBoxCard.Body className={"detail-card"}>
                <Display isVisible={state === DATA_STATE.isLoading}>
                    <Loader/>
                </Display>
                <Display isVisible={state === DATA_STATE.loaded}>
                    <Display isVisible={!isEmpty}>
                        <div className="widget-content">
                            <div className="user-management-chart-legend">
                                <Legend
                                    items={legendItems}
                                    selectedItemName={selectedElement}
                                    onItemHover={setSelectedElement}
                                    showValues={false}
                                />
                            </div>
                            <div className={"horizontal-chart"}>
                                <AutoSizer disableHeight style={{width: "auto"}}>
                                    {({width}) =>
                                        <HorizontalGroupBarChart
                                            width={width}
                                            height={490}
                                            color={null}
                                            data={horizontalGroupBarChart}
                                            xUnitOfMeasure={""}
                                            labelsOptions={{externalLegend: true}}
                                            onPathOver={setSelectedElement}
                                            selectedItem={selectedElement}
                                        />
                                    }
                                </AutoSizer>
                            </div>
                        </div>
                    </Display>
                    <Display isVisible={isEmpty}>
                        <div className={"vertical-align-center"}>
                            <EmptyState>
                                <EmptyState.InfoIcon
                                    styles={{
                                        root: {
                                            color: "var(--empty-indicator-svg-stroke)",
                                        }
                                    }}
                                    glyph={infoIcons.NoContent}
                                />
                                <EmptyState.Title color="var(--empty-indicator-title-color)">{AppTranslations.noDataAvailable}</EmptyState.Title>
                                <EmptyState.SubTitle color="var(--empty-indicator-subtitle-color)">{AppTranslations.miscellaneous.checkFiltersMessage}</EmptyState.SubTitle>
                            </EmptyState>
                        </div>
                    </Display>
                </Display>
                <Display isVisible={state === DATA_STATE.error}>
                    <EmptyState>
                        <EmptyState.Icon size={144} glyph={icons.ERROR_CIRCLE}/>
                        <EmptyState.Title color="var(--empty-indicator-title-color)">{AppTranslations.errorMessages.errorTitle}</EmptyState.Title>
                        <EmptyState.SubTitle color="inherit">
                            {AppTranslations.errorMessages.tryAgainLaterMessage}
                        </EmptyState.SubTitle>
                    </EmptyState>
                </Display>
            </VeBoxCard.Body>
        </VeBoxCard>
    )
}

